import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Link,
  ButtonGroup,
  SidePanel,
  Text,
  Spinner,
  Snackbar,
  Snack,
  Tooltip,
} from '@nike/eds'
import { FormProvider, useForm } from 'react-hook-form'
import {
  DatePicker,
  InputGroup,
  TextArea,
  Checkbox as EdsCheckbox,
} from '../../../common/components/form'
import { useCarrierHubList } from '../../../common/hooks/useCarrierHubList'
import { CarrierHubTreeDropdown } from '../../../common/components/carrierhubtreedropdown/CarrierHubTreeDropdown'
import { RebookingDeliveryViewFilter } from '../../../services/ShipmentService'
import { useCountriesList } from '../../../common/hooks/useCountriesList'
import './FilterForm.css'
import {
  ApplicationSettings,
  updateUserPreferences,
  ApplicationFilter,
} from '../../../services/UserPreferencesService'
import { useOktaAuth } from '@okta/okta-react'
import {
  mapStringToArrayOrUndefined,
  valuesAreAllNumbers,
} from '../../../common/utils/datautils/utils'
import { CountriesAutoComplete } from '../../../common/components/countriesautocomplete/CountriesAutoComplete'

export const PACKLIST_TYPES = {
  ALL_PACKLISTS: 'All',
  REBOOKED_HOLD: 'Rebooked / On Hold',
}

export const idpOptions = [
  { value: 'all', label: 'All' },
  { value: 'idp', label: 'IDP' },
  { value: 'nonIdp', label: 'NON-IDP' },
]

const emptyFormData = {
  carrierHubs: [],
  shipmentReferences: '',
  shipToCodes: '',
  iDPPlannedFromTimestamp: '',
  iDPPlannedToTimestamp: '',
  plannedGoodsIssueFromTimestamp: '',
  plannedGoodsIssueToTimestamp: '',
  countries: [],
  typeOfPacklist: PACKLIST_TYPES.ALL_PACKLISTS,
  rebookedCheckbox: '',
  holdCheckbox: '',
  idpCheckbox: '',
  nonIdpCheckbox: '',
}

const useFilterForm = (
  isOpen,
  initData: RebookingDeliveryViewFilter,
  isCountriesCallSuccess,
  countryData
) => {
  const _formData = useMemo(() => {
    const {
      carrierHubs = emptyFormData.carrierHubs,
      shipmentReferences = [],
      shipToCodes = [],
      iDPPlannedFromTimestamp = emptyFormData.iDPPlannedFromTimestamp,
      iDPPlannedToTimestamp = emptyFormData.iDPPlannedToTimestamp,
      plannedGoodsIssueFromTimestamp = emptyFormData.plannedGoodsIssueFromTimestamp,
      plannedGoodsIssueToTimestamp = emptyFormData.plannedGoodsIssueToTimestamp,
      countries = [],
      rebooked = false,
      rebookedCheckbox = emptyFormData.rebookedCheckbox,
      holdCheckbox = emptyFormData.holdCheckbox,
      idpCheckbox = emptyFormData.idpCheckbox,
      nonIdpCheckbox = emptyFormData.nonIdpCheckbox,
    } = initData ?? {}
    return isOpen
      ? {
          carrierHubs,
          shipmentReferences: shipmentReferences.join('\n') ?? emptyFormData.shipmentReferences,
          shipToCodes: shipToCodes.join('\n') ?? emptyFormData.shipToCodes,
          iDPPlannedFromTimestamp,
          iDPPlannedToTimestamp,
          plannedGoodsIssueFromTimestamp,
          plannedGoodsIssueToTimestamp,
          countries:
            isCountriesCallSuccess && countryData
              ? countries.map(code => countryData.find(country => country.code === code))
              : emptyFormData.countries,
          typeOfPacklist: rebooked ? PACKLIST_TYPES.REBOOKED_HOLD : PACKLIST_TYPES.ALL_PACKLISTS,
          rebookedCheckbox: rebookedCheckbox,
          holdCheckbox: holdCheckbox,
          idpCheckbox: idpCheckbox,
          nonIdpCheckbox: nonIdpCheckbox,
        }
      : emptyFormData
  }, [isOpen, initData, isCountriesCallSuccess, countryData])

  const { reset, resetField, getValues, ...restForm } = useForm({
    defaultValues: _formData,
    mode: 'all',
  })

  useEffect(() => {
    // refresh the form state whenever we open it
    if (isOpen) {
      reset(_formData)
    }
  }, [isOpen, reset, _formData])

  // extra utility functions...
  const clear = useCallback(() => reset(emptyFormData), [reset])

  return {
    ...restForm,
    reset,
    clear,
    resetField,
    getValues,
  }
}

export interface FilterProps {
  currentFilter: RebookingDeliveryViewFilter
  onClickFilter: (formData: RebookingDeliveryViewFilter) => void
  expand: boolean
  onCloseSidePanel: () => void
}

export const FilterForm = ({
  currentFilter: initData,
  onClickFilter,
  expand = false,
  onCloseSidePanel,
}: FilterProps): JSX.Element => {
  const { data: carrierHubData, isSuccess: isCarrierHubCallSuccess } = useCarrierHubList()
  const { data: countryData, isSuccess: isCountriesCallSuccess } = useCountriesList()
  const filterformMethods = useFilterForm(expand, initData, isCountriesCallSuccess, countryData)
  const { control, handleSubmit, clear, reset } = filterformMethods
  const { authState } = useOktaAuth()
  const [isSaving, setIsSaving] = useState(false)
  const [appliedFilterSnackOpen, setAppliedFilterSnackOpen] = useState(false)

  const createFilter = data => {
    return {
      carrierHubs: data.carrierHubs?.length ? data.carrierHubs : undefined,
      shipmentReferences: mapStringToArrayOrUndefined(data.shipmentReferences),
      shipToCodes: mapStringToArrayOrUndefined(data.shipToCodes),
      rebooked: data.typeOfPacklist === PACKLIST_TYPES.REBOOKED_HOLD ? true : undefined,
      idpIndicator: data.idpIndicator,
      countries: data.countries?.length ? data.countries.map(c => c.code) : undefined,
      iDPPlannedFromTimestamp: data.iDPPlannedFromTimestamp
        ? data.iDPPlannedFromTimestamp
        : undefined,
      iDPPlannedToTimestamp: data.iDPPlannedToTimestamp ? data.iDPPlannedToTimestamp : undefined,
      plannedGoodsIssueFromTimestamp: data.plannedGoodsIssueFromTimestamp
        ? data.plannedGoodsIssueFromTimestamp
        : undefined,
      plannedGoodsIssueToTimestamp: data.plannedGoodsIssueToTimestamp
        ? data.plannedGoodsIssueToTimestamp
        : undefined,
      rebookedCheckbox: data.rebookedCheckbox ? data.rebookedCheckbox : undefined,
      holdCheckbox: data.holdCheckbox ? data.holdCheckbox : undefined,
      idpCheckbox: data.idpCheckbox ? data.idpCheckbox : undefined,
      nonIdpCheckbox: data.nonIdpCheckbox ? data.nonIdpCheckbox : undefined,
      datePickerType: data.datePickerType ? data.datePickerType : undefined,
    }
  }

  const applyFilters = (data, setDefault: boolean) => {
    if (setDefault) {
      handlePostFilter(data)
    }
    // do some data sanitization before updating the higher state
    onClickFilter(createFilter(data))
    onCloseSidePanel()
  }

  const closeAndResetFilters = () => {
    reset()
    onCloseSidePanel()
  }

  const handlePostFilter = async data => {
    setIsSaving(true)
    const defaultFilter: ApplicationFilter = {
      name: 'default',
      default: true,
      filter: createFilter(data),
    }
    const applicationSettings: ApplicationSettings = {
      filters: [defaultFilter],
    }
    await updateUserPreferences(authState, applicationSettings, 'shipmentReport', 'filter')
    setIsSaving(false)
    setAppliedFilterSnackOpen(true)
    setTimeout(() => {
      setAppliedFilterSnackOpen(false)
    }, 3000)
  }

  const formId = 'shipmentFilterForm'
  return (
    // use FormProvider to pass all methods from useForm to all the underlying components
    <FormProvider {...filterformMethods}>
      <SidePanel
        onDismiss={closeAndResetFilters}
        isOpen={expand}
        hasScrim={false}
        headerSlot={
          <>
            <Text font="title-2" as="h2">
              Filter
            </Text>
            <span className="eds-type--body-2">
              <Link as="button" variant="secondary" onClick={clear}>
                Clear All
              </Link>
            </span>
          </>
        }
        footerSlot={
          <ButtonGroup>
            <Button variant="primary" type="submit" size="small" form={formId}>
              Apply
            </Button>

            <Tooltip
              bodySlot={<Text>Save as default filter and apply</Text>}
              isDark={true}
              placement={'right'}
            >
              <Button
                form={formId}
                type="submit"
                variant="secondary"
                size="small"
                onClick={async () => {
                  await handleSubmit(data => applyFilters(data, true))()
                }}
                disabled={isSaving}
              >
                {isSaving && <Spinner />}
                {isSaving ? 'Saving...' : 'Save as default'}
              </Button>
            </Tooltip>
          </ButtonGroup>
        }
      >
        <form
          id={formId}
          className="filter-form"
          onSubmit={handleSubmit(data => applyFilters(data, false))}
        >
          <InputGroup label="Carrier / Hub" clearable={false} withDivider={false}>
            <CarrierHubTreeDropdown
              name="carrierHubs"
              control={control}
              carrierHubs={isCarrierHubCallSuccess && carrierHubData ? carrierHubData : []}
              showPartiallySelected={true}
            />
          </InputGroup>

          <InputGroup label="Packlist" clearable={false} withDivider={false}>
            <TextArea
              name="shipmentReferences"
              control={control}
              resize="vertical"
              // @ts-ignore
              style={{ padding: 'var(--eds-space-4)' }}
              label="Packlist"
              rules={{
                validate: input =>
                  valuesAreAllNumbers(input) || 'Packlist can only contain numeric values',
              }}
            />
          </InputGroup>

          <InputGroup label="Ship To Number" clearable={false} withDivider={false}>
            <TextArea
              name="shipToCodes"
              control={control}
              resize="vertical"
              // @ts-ignore
              style={{ padding: 'var(--eds-space-4)' }}
              label="Ship To Number"
              rules={{
                validate: input =>
                  valuesAreAllNumbers(input) || 'Ship To Number can only contain numeric values',
              }}
            />
          </InputGroup>

          <DatePicker
            control={control}
            from={{
              name: 'plannedGoodsIssueFromTimestamp',
            }}
            to={{
              name: 'plannedGoodsIssueToTimestamp',
            }}
            title={'Planned Goods Issue Date'}
            advanced={true}
          />

          <DatePicker
            control={control}
            from={{
              name: 'iDPPlannedFromTimestamp',
            }}
            to={{
              name: 'iDPPlannedToTimestamp',
            }}
            title={'Expected Delivery Date'}
            advanced={true}
          />

          <InputGroup label="Ship To Country" clearable={false} withDivider={false}>
            <CountriesAutoComplete
              name="countries"
              control={control}
              countryData={isCountriesCallSuccess && countryData ? countryData : []}
            />
          </InputGroup>
          <InputGroup label="Rebooking Type" clearable={false} withDivider={false}>
            <EdsCheckbox
              name="rebookedCheckbox"
              control={control}
              label="Rebooked"
              value="rebooked"
            />
            <br />
            <EdsCheckbox name="holdCheckbox" control={control} label="Hold" value="hold" />
          </InputGroup>

          <InputGroup label="IDP Indicator" clearable={false} withDivider={false}>
            <EdsCheckbox name="idpCheckbox" control={control} label="IDP" value="idp" />
            <br />
            <EdsCheckbox name="nonIdpCheckbox" control={control} label="Non-IDP" value="nonIdp" />
          </InputGroup>
        </form>
      </SidePanel>

      <Snackbar>
        {appliedFilterSnackOpen && (
          <Snack id="1" status="success" onDismiss={id => setAppliedFilterSnackOpen(false)}>
            Filter saved!
          </Snack>
        )}
      </Snackbar>
    </FormProvider>
  )
}
